/**
 * Player Relationship application service — Friend workflow (Phase 1).
 * Arena / Competition / Club consume this; they do not own relationship logic.
 */

import { getCurrentAuthUser } from "../repositories/authRepository";
import * as playerRepository from "../repositories/playerRepository";
import * as playerRelationshipRepository from "../repositories/playerRelationshipRepository";
import { ensureMyPlayer } from "./playerProfileService";
import { searchPlayerDirectory } from "./playerEcosystemService";
import { notify } from "./notificationService";
import { NOTIFICATION_KINDS } from "../domain/notifications/notificationTypes";
import {
  RELATIONSHIP_STATUSES,
  RELATIONSHIP_TYPES,
  buildPlayerRelationshipDocument,
  buildRelationshipPairKey,
  otherPlayerIdInRelationship,
  resolveFriendSearchState,
  sortPlayersByRelationshipPriority,
} from "../domain/playerRelationship/playerRelationshipTypes";

function currentUid() {
  return getCurrentAuthUser()?.uid || null;
}

/**
 * @param {object} rel
 */
/**
 * @param {object} rel
 * @param {string} [recipientUserId]
 */
export function publishFriendRequestNotification(rel, recipientUserId) {
  if (!rel?.relationshipId) return null;
  const userId = recipientUserId || rel.toUserId || null;
  const payload = {
    kind: NOTIFICATION_KINDS.FRIEND_REQUEST,
    title: "Friend request",
    body: `${rel.fromPlayerName || "A player"} wants to be friends`,
    href: "/friends?tab=pending",
    entityType: "relationship",
    entityId: rel.relationshipId,
    relationshipId: rel.relationshipId,
    dedupeKey: `friend_request:${rel.relationshipId}`,
    userId: userId || undefined,
    meta: {
      relationshipId: rel.relationshipId,
      fromPlayerId: rel.fromPlayerId,
      fromPlayerName: rel.fromPlayerName,
      toPlayerId: rel.toPlayerId,
    },
  };
  if (userId) {
    return notify(payload);
  }
  // Local-only fallback (tests / unsigned)
  return notify(payload);
}

/**
 * Send a friend request to another registered player.
 * @param {{ toPlayerId: string }} args
 */
export async function sendFriendRequest({ toPlayerId }) {
  const uid = currentUid();
  if (!uid) throw new Error("Sign in required.");
  const me = await ensureMyPlayer(uid);
  const targetId = String(toPlayerId || "").trim();
  if (!targetId) throw new Error("Player required.");
  if (targetId === me.playerId) {
    const err = new Error("You can’t add yourself.");
    err.code = "self";
    throw err;
  }

  const target = await playerRepository.findById(targetId);
  if (!target || target.isActive === false) {
    const err = new Error("Player not found.");
    err.code = "not_found";
    throw err;
  }
  if (!target.userId) {
    const err = new Error("That player isn’t linked to an account yet.");
    err.code = "unlinked";
    throw err;
  }

  const pairKey = buildRelationshipPairKey(
    me.playerId,
    target.playerId,
    RELATIONSHIP_TYPES.FRIEND
  );
  const existing = await playerRelationshipRepository.findRelationshipByPairKey(
    pairKey
  );
  if (existing) {
    if (existing.status === RELATIONSHIP_STATUSES.ACCEPTED) {
      return { relationship: existing, reused: true, alreadyFriends: true };
    }
    if (existing.status === RELATIONSHIP_STATUSES.PENDING) {
      return { relationship: existing, reused: true };
    }
    // Declined / cancelled — allow a fresh pending request by updating.
    await playerRelationshipRepository.updatePlayerRelationship(
      existing.relationshipId,
      {
        status: RELATIONSHIP_STATUSES.PENDING,
        fromPlayerId: me.playerId,
        toPlayerId: target.playerId,
        playerId: me.playerId,
        relatedPlayerId: target.playerId,
        fromUserId: uid,
        toUserId: target.userId,
        fromPlayerName: me.displayName || "Player",
        toPlayerName: target.displayName || "Player",
        memberPlayerIds: [me.playerId, target.playerId],
        memberUserIds: [uid, target.userId],
        createdBy: uid,
        acceptedAtMs: null,
        declinedAtMs: null,
        createdAtMs: Date.now(),
      }
    );
    const refreshed =
      await playerRelationshipRepository.fetchPlayerRelationship(
        existing.relationshipId
      );
    if (target.userId) {
      void publishFriendRequestNotification(refreshed, target.userId);
    }
    return { relationship: refreshed, reused: false };
  }

  const relationship = buildPlayerRelationshipDocument({
    fromPlayerId: me.playerId,
    toPlayerId: target.playerId,
    fromUserId: uid,
    toUserId: target.userId,
    fromPlayerName: me.displayName || "Player",
    toPlayerName: target.displayName || "Player",
    createdBy: uid,
    relationshipType: RELATIONSHIP_TYPES.FRIEND,
  });
  await playerRelationshipRepository.createPlayerRelationship(relationship);

  if (target.userId) {
    void publishFriendRequestNotification(relationship, target.userId);
  }

  return { relationship, reused: false };
}

/**
 * @param {string} relationshipId
 */
export async function acceptFriendRequest(relationshipId) {
  try {
    const uid = currentUid();
    if (!uid) return { ok: false, reason: "auth_required" };
    const me = await ensureMyPlayer(uid);
    const rel = await playerRelationshipRepository.fetchPlayerRelationship(
      relationshipId
    );
    if (!rel) return { ok: false, reason: "not_found" };
    if (rel.relationshipType !== RELATIONSHIP_TYPES.FRIEND) {
      return { ok: false, reason: "unsupported" };
    }
    if (rel.status !== RELATIONSHIP_STATUSES.PENDING) {
      return { ok: false, reason: rel.status, relationship: rel };
    }
    if (rel.toPlayerId !== me.playerId && rel.toUserId !== uid) {
      return { ok: false, reason: "forbidden" };
    }

    const toUserId = rel.toUserId || uid;
    const toPlayerId = rel.toPlayerId || me.playerId;
    const memberUserIds = Array.from(
      new Set([rel.fromUserId, toUserId, ...(rel.memberUserIds || [])].filter(Boolean))
    );
    const memberPlayerIds = Array.from(
      new Set(
        [rel.fromPlayerId, toPlayerId, ...(rel.memberPlayerIds || [])].filter(Boolean)
      )
    );

    await playerRelationshipRepository.updatePlayerRelationship(relationshipId, {
      status: RELATIONSHIP_STATUSES.ACCEPTED,
      acceptedAtMs: Date.now(),
      // Keep both endpoints so each player's friends list query (from/to UserId) hits.
      fromUserId: rel.fromUserId || null,
      toUserId,
      memberUserIds:
        rel.fromUserId && toUserId ? [rel.fromUserId, toUserId] : memberUserIds,
      memberPlayerIds:
        rel.fromPlayerId && toPlayerId
          ? [rel.fromPlayerId, toPlayerId]
          : memberPlayerIds,
    });
    const updated = await playerRelationshipRepository.fetchPlayerRelationship(
      relationshipId
    );
    return { ok: true, relationship: updated };
  } catch (err) {
    return {
      ok: false,
      reason: "error",
      message: err?.message || "Could not accept friend request",
    };
  }
}

/**
 * @param {string} relationshipId
 */
export async function declineFriendRequest(relationshipId) {
  try {
    const uid = currentUid();
    if (!uid) return { ok: false, reason: "auth_required" };
    const me = await ensureMyPlayer(uid);
    const rel = await playerRelationshipRepository.fetchPlayerRelationship(
      relationshipId
    );
    if (!rel) return { ok: false, reason: "not_found" };
    if (rel.status !== RELATIONSHIP_STATUSES.PENDING) {
      return { ok: false, reason: rel.status, relationship: rel };
    }
    if (rel.toPlayerId !== me.playerId && rel.toUserId !== uid) {
      return { ok: false, reason: "forbidden" };
    }
    await playerRelationshipRepository.updatePlayerRelationship(relationshipId, {
      status: RELATIONSHIP_STATUSES.DECLINED,
      declinedAtMs: Date.now(),
    });
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      reason: "error",
      message: err?.message || "Could not decline friend request",
    };
  }
}

/**
 * Cancel an outgoing pending request.
 * @param {string} relationshipId
 */
export async function cancelFriendRequest(relationshipId) {
  const uid = currentUid();
  if (!uid) return { ok: false, reason: "auth_required" };
  const me = await ensureMyPlayer(uid);
  const rel = await playerRelationshipRepository.fetchPlayerRelationship(
    relationshipId
  );
  if (!rel) return { ok: false, reason: "not_found" };
  if (rel.status !== RELATIONSHIP_STATUSES.PENDING) {
    return { ok: false, reason: rel.status };
  }
  if (rel.fromPlayerId !== me.playerId && rel.createdBy !== uid) {
    return { ok: false, reason: "forbidden" };
  }
  await playerRelationshipRepository.updatePlayerRelationship(relationshipId, {
    status: RELATIONSHIP_STATUSES.CANCELLED,
  });
  return { ok: true };
}

/**
 * @param {string} [userId]
 */
export async function getFriendsPageModel(userId) {
  const uid = userId || currentUid();
  if (!uid) {
    return { friends: [], pendingReceived: [], pendingSent: [], friendsCount: 0 };
  }
  const me = await ensureMyPlayer(uid);
  const settled = await Promise.allSettled([
    playerRelationshipRepository.listAcceptedFriendsForPlayer(me.playerId, {
      userId: uid,
    }),
    playerRelationshipRepository.listPendingReceivedForPlayer(me.playerId, {
      userId: uid,
    }),
    playerRelationshipRepository.listPendingSentForPlayer(me.playerId, {
      userId: uid,
    }),
  ]);
  const accepted =
    settled[0].status === "fulfilled" ? settled[0].value || [] : [];
  const received =
    settled[1].status === "fulfilled" ? settled[1].value || [] : [];
  const sent =
    settled[2].status === "fulfilled" ? settled[2].value || [] : [];
  if (settled.some((r) => r.status === "rejected")) {
    console.warn(
      "[friends] partial load failure",
      settled.map((r) => (r.status === "rejected" ? r.reason?.message || r.reason : null))
    );
  }

  const enrich = async (rels, side) => {
    const rows = [];
    for (const rel of rels) {
      const otherId =
        side === "friend"
          ? otherPlayerIdInRelationship(rel, me.playerId)
          : side === "received"
            ? rel.fromPlayerId
            : rel.toPlayerId;
      const player = otherId ? await playerRepository.findById(otherId) : null;
      rows.push({
        relationship: rel,
        player: player
          ? {
              playerId: player.playerId,
              displayName: player.displayName || "Player",
              username: player.username || null,
              userId: player.userId || null,
              profilePhoto: player.profilePhoto || null,
              lastActiveAtMs: player.lastActiveAtMs || player.updatedAtMs || null,
            }
          : {
              playerId: otherId,
              displayName:
                side === "received"
                  ? rel.fromPlayerName || "Player"
                  : rel.toPlayerName || "Player",
              username: null,
              userId: null,
              profilePhoto: null,
              lastActiveAtMs: null,
            },
      });
    }
    return rows;
  };

  const friends = await enrich(accepted, "friend");
  const pendingReceived = await enrich(received, "received");
  const pendingSent = await enrich(sent, "sent");

  return {
    viewerPlayerId: me.playerId,
    friends,
    pendingReceived,
    pendingSent,
    friendsCount: friends.length,
  };
}

/**
 * @param {string} playerId
 */
export async function countAcceptedFriends(playerId) {
  if (!playerId) return 0;
  const list =
    await playerRelationshipRepository.listAcceptedFriendsForPlayer(playerId, {
      max: 120,
    });
  return list.length;
}

/**
 * Directory search enriched with friend relationship state + ranking.
 * @param {{
 *   query?: string;
 *   limit?: number;
 *   viewerUserId?: string | null;
 *   friendsOnly?: boolean;
 * }} options
 */
export async function searchPlayersWithRelationships(options = {}) {
  const uid = options.viewerUserId || currentUid();
  const page = await searchPlayerDirectory({
    query: options.query || "",
    limit: options.limit || 20,
    offset: options.offset || 0,
  });

  let viewerPlayerId = null;
  /** @type {Map<string, object>} */
  const byOther = new Map();

  if (uid) {
    try {
      const me = await ensureMyPlayer(uid);
      viewerPlayerId = me.playerId;
      const rels =
        await playerRelationshipRepository.listRelationshipsForPlayer(
          me.playerId
        );
      rels.forEach((rel) => {
        if (
          rel.status !== RELATIONSHIP_STATUSES.ACCEPTED &&
          rel.status !== RELATIONSHIP_STATUSES.PENDING
        ) {
          return;
        }
        const other = otherPlayerIdInRelationship(rel, me.playerId);
        if (other) byOther.set(other, rel);
      });
    } catch {
      /* anonymous / no player */
    }
  }

  let items = (page.items || [])
    .filter((p) => p.playerId !== viewerPlayerId)
    .map((p) => {
      const rel = byOther.get(p.playerId) || null;
      const friendState = resolveFriendSearchState(rel, viewerPlayerId);
      return {
        ...p,
        username: p.username || null,
        relationship: rel,
        friendState,
        relationshipId: rel?.relationshipId || null,
      };
    });

  if (options.friendsOnly) {
    items = items.filter((p) => p.friendState === "friend");
  }

  items = sortPlayersByRelationshipPriority(items, (p) => p.friendState);

  return {
    ...page,
    items,
    viewerPlayerId,
  };
}

/**
 * Accepted friends as invite candidates (Arena Invite Friends).
 */
export async function listFriendInviteCandidates(userId) {
  const model = await getFriendsPageModel(userId);
  return (model.friends || [])
    .map((row) => row.player)
    .filter((p) => p?.playerId && p?.userId);
}

export {
  listAcceptedFriendsForPlayer,
  listPendingReceivedForPlayer,
  listPendingSentForPlayer,
  subscribePendingFriendRequestsForUser,
  subscribeAcceptedFriends,
} from "../repositories/playerRelationshipRepository";
